/**
 * ============================================================================
 * [P6-02] useTrackingPosts Hook
 *
 * 트래킹 포스트 데이터 관리를 위한 커스텀 훅
 *
 * 기능:
 * - 실시간 구독으로 포스트 목록 동기화
 * - CRUD 함수 (createPost, editPost, removePost)
 * - 필터/정렬 적용
 * - CSV 내보내기
 *
 * Safety:
 * - useEffect cleanup으로 메모리 누수 방지
 * - 낙관적 업데이트 + 에러 시 롤백
 * ============================================================================
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from './useAuth'
import {
  createPost as createPostService,
  updatePost as updatePostService,
  deletePost as deletePostService,
  subscribeToPosts,
  addMetricToPost,
  updateMetricInPost,
  deleteMetricFromPost,
  batchDeleteMetricsFromPost, // [P3-03] 일괄 삭제용
} from '@/services/firestore'
import type {
  TrackingPost,
  TrackingMetric,
  TrackingFilterState,
  TrackingSortOption,
} from '@/types'

// ============================================================================
// 기본 필터 상태
// ============================================================================
export const DEFAULT_TRACKING_FILTER: TrackingFilterState = {
  search: '',
  sortBy: 'updatedDesc',
  status: 'all',
}

// ============================================================================
// 반환 타입 정의
// ============================================================================
export interface UseTrackingPostsReturn {
  // State
  posts: TrackingPost[]
  filteredPosts: TrackingPost[]
  isLoading: boolean
  error: Error | null

  // Filter
  filter: TrackingFilterState
  setFilter: (filter: Partial<TrackingFilterState>) => void
  resetFilter: () => void

  // CRUD
  createPost: (post: Omit<TrackingPost, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => Promise<TrackingPost | null>
  editPost: (id: string, updates: Partial<TrackingPost>) => Promise<boolean>
  removePost: (id: string) => Promise<boolean>
  toggleFavorite: (id: string) => Promise<boolean>
  toggleActive: (id: string) => Promise<boolean>

  // Metrics
  addMetric: (postId: string, metric: Omit<TrackingMetric, 'id'>) => Promise<boolean>
  updateMetric: (postId: string, metricId: string, updates: Partial<TrackingMetric>) => Promise<boolean>
  deleteMetric: (postId: string, metricId: string) => Promise<boolean>

  // Export
  exportToCSV: () => void

  // [P3-04] Batch Selection - 메트릭 일괄 선택/삭제
  selectedMetrics: string[]
  setSelectedMetrics: (ids: string[]) => void
  toggleMetricSelection: (metricId: string) => void
  selectAllMetrics: (postId: string) => void
  clearMetricSelection: () => void
  batchDeleteMetrics: (postId: string, metricIds: string[]) => Promise<boolean>
}

// ============================================================================
// 헬퍼: 포스트의 최신 메트릭 가져오기
// ============================================================================
function getLatestMetric(post: TrackingPost): TrackingMetric | null {
  if (!post.metrics || post.metrics.length === 0) return null
  return [...post.metrics].sort((a, b) => b.date.localeCompare(a.date))[0]
}

// ============================================================================
// 헬퍼: 메트릭 합계 계산
// ============================================================================
function getTotalMetrics(post: TrackingPost) {
  if (!post.metrics || post.metrics.length === 0) {
    return { views: 0, likes: 0, comments: 0, shares: 0, follows: 0 }
  }

  const latest = getLatestMetric(post)
  return {
    views: latest?.views ?? 0,
    likes: latest?.likes ?? 0,
    comments: latest?.comments ?? 0,
    shares: latest?.shares ?? 0,
    follows: latest?.follows ?? 0,
  }
}

// ============================================================================
// [P6-02-A] useTrackingPosts Hook 구현
// ============================================================================
export function useTrackingPosts(): UseTrackingPostsReturn {
  const { user } = useAuth()

  // ========================================================================
  // Local State
  // ========================================================================
  const [posts, setPosts] = useState<TrackingPost[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [filter, setFilterState] = useState<TrackingFilterState>(DEFAULT_TRACKING_FILTER)

  // [P3-01] 다중 메트릭 선택 상태 (일괄 삭제용)
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([])

  // ========================================================================
  // [P3-02] 메트릭 일괄 선택 함수
  // ========================================================================

  // 개별 메트릭 선택 토글
  const toggleMetricSelection = useCallback((metricId: string) => {
    setSelectedMetrics((prev) => {
      if (prev.includes(metricId)) {
        return prev.filter((id) => id !== metricId)
      }
      return [...prev, metricId]
    })
  }, [])

  // 포스트의 전체 메트릭 선택
  const selectAllMetrics = useCallback((postId: string) => {
    const post = posts.find((p) => p.id === postId)
    if (!post || !post.metrics) return

    const allMetricIds = post.metrics.map((m) => m.id)
    setSelectedMetrics(allMetricIds)
  }, [posts])

  // 선택 초기화
  const clearMetricSelection = useCallback(() => {
    setSelectedMetrics([])
  }, [])

  // ========================================================================
  // [P3-03] 메트릭 일괄 삭제
  // - 낙관적 업데이트 + 실패 시 롤백
  // - batchDeleteMetricsFromPost 서비스 함수 사용
  // ========================================================================
  const batchDeleteMetrics = useCallback(
    async (postId: string, metricIds: string[]): Promise<boolean> => {
      // 1. 인증 확인
      if (!user) {
        console.warn('[useTrackingPosts.batchDeleteMetrics] User not authenticated')
        return false
      }

      // 2. 유효성 검사
      if (!postId || metricIds.length === 0) {
        console.warn('[useTrackingPosts.batchDeleteMetrics] Invalid parameters')
        return false
      }

      // 3. 원본 포스트 백업 (롤백용)
      const originalPost = posts.find((p) => p.id === postId)
      if (!originalPost) {
        setError(new Error('Post not found'))
        return false
      }

      try {
        // 4. 낙관적 업데이트: UI 먼저 반영
        const metricIdSet = new Set(metricIds)
        setPosts((prev) =>
          prev.map((p) =>
            p.id === postId
              ? {
                  ...p,
                  metrics: p.metrics.filter((m) => !metricIdSet.has(m.id)),
                }
              : p
          )
        )

        // 5. 선택 상태 초기화
        clearMetricSelection()

        // 6. 서버에 반영
        await batchDeleteMetricsFromPost(user.uid, postId, metricIds)

        setError(null)
        return true
      } catch (err) {
        // 7. 실패 시 롤백
        setPosts((prev) =>
          prev.map((p) => (p.id === postId ? originalPost : p))
        )

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useTrackingPosts.batchDeleteMetrics] Error:', error)
        return false
      }
    },
    [user, posts, clearMetricSelection]
  )

  // ========================================================================
  // [P6-02-B] 실시간 구독
  // ========================================================================
  useEffect(() => {
    if (!user) {
      setPosts([])
      setIsLoading(false)
      setError(null)
      return
    }

    setIsLoading(true)

    const unsubscribe = subscribeToPosts(
      user.uid,
      (newPosts) => {
        setPosts(newPosts)
        setIsLoading(false)
        setError(null)
      },
      (err) => {
        setError(err)
        setIsLoading(false)
      }
    )

    return () => {
      unsubscribe()
    }
  }, [user?.uid])

  // ========================================================================
  // [P6-02-C] 필터 & 정렬 적용
  // ========================================================================
  const filteredPosts = useMemo(() => {
    let result = [...posts]

    // 1. 검색 필터
    if (filter.search) {
      const searchLower = filter.search.toLowerCase()
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(searchLower) ||
          p.content?.toLowerCase().includes(searchLower) ||
          p.platform.toLowerCase().includes(searchLower)
      )
    }

    // 2. 상태 필터
    switch (filter.status) {
      case 'active':
        result = result.filter((p) => p.isActive !== false)
        break
      case 'inactive':
        result = result.filter((p) => p.isActive === false)
        break
      case 'hasData':
        result = result.filter((p) => p.metrics && p.metrics.length > 0)
        break
      case 'noData':
        result = result.filter((p) => !p.metrics || p.metrics.length === 0)
        break
    }

    // 3. 날짜 범위 필터 (업데이트 날짜 기준)
    if (filter.dateFrom) {
      const fromDate = new Date(filter.dateFrom)
      result = result.filter((p) => {
        const updated = typeof p.updatedAt === 'string' ? new Date(p.updatedAt) : p.updatedAt
        return updated >= fromDate
      })
    }
    if (filter.dateTo) {
      const toDate = new Date(filter.dateTo)
      toDate.setHours(23, 59, 59, 999)
      result = result.filter((p) => {
        const updated = typeof p.updatedAt === 'string' ? new Date(p.updatedAt) : p.updatedAt
        return updated <= toDate
      })
    }

    // 4. 범위 필터 (조회수, 좋아요 등)
    result = result.filter((p) => {
      const metrics = getTotalMetrics(p)
      if (filter.minViews !== undefined && metrics.views < filter.minViews) return false
      if (filter.maxViews !== undefined && metrics.views > filter.maxViews) return false
      if (filter.minLikes !== undefined && metrics.likes < filter.minLikes) return false
      if (filter.maxLikes !== undefined && metrics.likes > filter.maxLikes) return false
      if (filter.minComments !== undefined && metrics.comments < filter.minComments) return false
      if (filter.maxComments !== undefined && metrics.comments > filter.maxComments) return false
      if (filter.minShares !== undefined && metrics.shares < filter.minShares) return false
      if (filter.maxShares !== undefined && metrics.shares > filter.maxShares) return false
      if (filter.minFollows !== undefined && metrics.follows < filter.minFollows) return false
      if (filter.maxFollows !== undefined && metrics.follows > filter.maxFollows) return false
      return true
    })

    // 5. 정렬
    result.sort((a, b) => {
      const metricsA = getTotalMetrics(a)
      const metricsB = getTotalMetrics(b)
      const updatedA = typeof a.updatedAt === 'string' ? new Date(a.updatedAt) : a.updatedAt
      const updatedB = typeof b.updatedAt === 'string' ? new Date(b.updatedAt) : b.updatedAt

      switch (filter.sortBy as TrackingSortOption) {
        case 'updatedAsc':
          return updatedA.getTime() - updatedB.getTime()
        case 'updatedDesc':
          return updatedB.getTime() - updatedA.getTime()
        case 'viewsDesc':
          return metricsB.views - metricsA.views
        case 'viewsAsc':
          return metricsA.views - metricsB.views
        case 'likesDesc':
          return metricsB.likes - metricsA.likes
        case 'likesAsc':
          return metricsA.likes - metricsB.likes
        case 'commentsDesc':
          return metricsB.comments - metricsA.comments
        case 'commentsAsc':
          return metricsA.comments - metricsB.comments
        case 'sharesDesc':
          return metricsB.shares - metricsA.shares
        case 'sharesAsc':
          return metricsA.shares - metricsB.shares
        case 'followsDesc':
          return metricsB.follows - metricsA.follows
        case 'followsAsc':
          return metricsA.follows - metricsB.follows
        case 'entriesDesc':
          return (b.metrics?.length ?? 0) - (a.metrics?.length ?? 0)
        case 'entriesAsc':
          return (a.metrics?.length ?? 0) - (b.metrics?.length ?? 0)
        case 'favoritesFirst':
          if (a.isFavorite && !b.isFavorite) return -1
          if (!a.isFavorite && b.isFavorite) return 1
          return updatedB.getTime() - updatedA.getTime()
        default:
          return updatedB.getTime() - updatedA.getTime()
      }
    })

    return result
  }, [posts, filter])

  // ========================================================================
  // [P6-02-D] Filter Functions
  // ========================================================================
  const setFilter = useCallback((newFilter: Partial<TrackingFilterState>) => {
    setFilterState((prev) => ({ ...prev, ...newFilter }))
  }, [])

  const resetFilter = useCallback(() => {
    setFilterState(DEFAULT_TRACKING_FILTER)
  }, [])

  // ========================================================================
  // [P6-02-E] createPost
  // ========================================================================
  const createPost = useCallback(
    async (
      postData: Omit<TrackingPost, 'id' | 'createdAt' | 'updatedAt' | 'userId'>
    ): Promise<TrackingPost | null> => {
      if (!user) {
        console.warn('[useTrackingPosts.createPost] User not authenticated')
        return null
      }

      if (!postData.title?.trim()) {
        setError(new Error('Title is required'))
        return null
      }

      try {
        const newPost = await createPostService(user.uid, postData)
        setError(null)
        return newPost
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useTrackingPosts.createPost] Error:', error)
        return null
      }
    },
    [user]
  )

  // ========================================================================
  // [P6-02-F] editPost
  // ========================================================================
  const editPost = useCallback(
    async (id: string, updates: Partial<TrackingPost>): Promise<boolean> => {
      if (!user) {
        console.warn('[useTrackingPosts.editPost] User not authenticated')
        return false
      }

      if (!id) {
        setError(new Error('Post ID is required'))
        return false
      }

      const originalPost = posts.find((p) => p.id === id)
      if (!originalPost) {
        setError(new Error('Post not found'))
        return false
      }

      try {
        // 낙관적 업데이트
        setPosts((prev) =>
          prev.map((p) => (p.id === id ? { ...p, ...updates } : p))
        )

        await updatePostService(user.uid, id, updates)
        setError(null)
        return true
      } catch (err) {
        // 롤백
        setPosts((prev) =>
          prev.map((p) => (p.id === id ? originalPost : p))
        )

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useTrackingPosts.editPost] Error:', error)
        return false
      }
    },
    [user, posts]
  )

  // ========================================================================
  // [P6-02-G] removePost
  // ========================================================================
  const removePost = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user) {
        console.warn('[useTrackingPosts.removePost] User not authenticated')
        return false
      }

      if (!id) {
        setError(new Error('Post ID is required'))
        return false
      }

      const originalPost = posts.find((p) => p.id === id)
      if (!originalPost) {
        setError(new Error('Post not found'))
        return false
      }

      try {
        // 낙관적 삭제
        setPosts((prev) => prev.filter((p) => p.id !== id))

        await deletePostService(user.uid, id)
        setError(null)
        return true
      } catch (err) {
        // 롤백
        setPosts((prev) => [...prev, originalPost])

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        console.error('[useTrackingPosts.removePost] Error:', error)
        return false
      }
    },
    [user, posts]
  )

  // ========================================================================
  // [P6-02-H] toggleFavorite
  // ========================================================================
  const toggleFavorite = useCallback(
    async (id: string): Promise<boolean> => {
      const post = posts.find((p) => p.id === id)
      if (!post) return false
      return editPost(id, { isFavorite: !post.isFavorite })
    },
    [posts, editPost]
  )

  // ========================================================================
  // [P6-02-I] toggleActive
  // ========================================================================
  const toggleActive = useCallback(
    async (id: string): Promise<boolean> => {
      const post = posts.find((p) => p.id === id)
      if (!post) return false
      return editPost(id, { isActive: post.isActive === false ? true : false })
    },
    [posts, editPost]
  )

  // ========================================================================
  // [P6-02-J] Metrics Functions
  // ========================================================================
  const addMetric = useCallback(
    async (postId: string, metric: Omit<TrackingMetric, 'id'>): Promise<boolean> => {
      if (!user) return false
      try {
        await addMetricToPost(user.uid, postId, metric)
        return true
      } catch (err) {
        console.error('[useTrackingPosts.addMetric] Error:', err)
        return false
      }
    },
    [user]
  )

  const updateMetric = useCallback(
    async (postId: string, metricId: string, updates: Partial<TrackingMetric>): Promise<boolean> => {
      if (!user) return false
      try {
        await updateMetricInPost(user.uid, postId, metricId, updates)
        return true
      } catch (err) {
        console.error('[useTrackingPosts.updateMetric] Error:', err)
        return false
      }
    },
    [user]
  )

  const deleteMetric = useCallback(
    async (postId: string, metricId: string): Promise<boolean> => {
      if (!user) return false
      try {
        await deleteMetricFromPost(user.uid, postId, metricId)
        return true
      } catch (err) {
        console.error('[useTrackingPosts.deleteMetric] Error:', err)
        return false
      }
    },
    [user]
  )

  // ========================================================================
  // [P6-02-K] CSV Export
  // ========================================================================
  const exportToCSV = useCallback(() => {
    const headers = [
      'ID',
      '제목',
      '플랫폼',
      'URL',
      '조회수',
      '좋아요',
      '댓글',
      '공유',
      '팔로우',
      '기록수',
      '생성일',
      '수정일',
      '즐겨찾기',
      '활성',
    ]

    const rows = filteredPosts.map((post) => {
      const metrics = getTotalMetrics(post)
      const createdAt = typeof post.createdAt === 'string'
        ? post.createdAt
        : post.createdAt.toISOString()
      const updatedAt = typeof post.updatedAt === 'string'
        ? post.updatedAt
        : post.updatedAt.toISOString()

      return [
        post.id,
        post.title,
        post.platform,
        post.url ?? '',
        metrics.views,
        metrics.likes,
        metrics.comments,
        metrics.shares,
        metrics.follows,
        post.metrics?.length ?? 0,
        createdAt,
        updatedAt,
        post.isFavorite ? 'Y' : 'N',
        post.isActive !== false ? 'Y' : 'N',
      ].join(',')
    })

    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `tracking_posts_${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }, [filteredPosts])

  // ========================================================================
  // Return
  // ========================================================================
  return {
    // State
    posts,
    filteredPosts,
    isLoading,
    error,

    // Filter
    filter,
    setFilter,
    resetFilter,

    // CRUD
    createPost,
    editPost,
    removePost,
    toggleFavorite,
    toggleActive,

    // Metrics
    addMetric,
    updateMetric,
    deleteMetric,

    // Export
    exportToCSV,

    // [P3-04] Batch Selection - 메트릭 일괄 선택/삭제
    selectedMetrics,
    setSelectedMetrics,
    toggleMetricSelection,
    selectAllMetrics,
    clearMetricSelection,
    batchDeleteMetrics,
  }
}

export default useTrackingPosts
